import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { operatorPauseTradeGroup, operatorResumeTradeGroup } from '../api/tradeGroups';
// Reuse the canonical confirm-modal chrome + .modal-pill-* button family (sibling-first).
import './CancelTradeGroupModal.css';

interface OperatorPauseResumeModalProps {
  tradeGroupId: string;
  /** 'pause' transitions an active group to OPERATOR_PAUSED; 'resume' returns it to WATCHING. */
  action: 'pause' | 'resume';
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Confirm modal for operator pause/resume (REQ-014). Mirrors CancelTradeGroupModal's
 * chrome, but both actions are NON-DESTRUCTIVE so the primary button uses the purple
 * constructive variant `.modal-pill-primary` — never the red `.modal-pill-apply`.
 */
const OperatorPauseResumeModal: React.FC<OperatorPauseResumeModalProps> = ({
  tradeGroupId,
  action,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const isPause = action === 'pause';

  const mutation = useMutation({
    mutationFn: () =>
      isPause
        ? operatorPauseTradeGroup(tradeGroupId, reason)
        : operatorResumeTradeGroup(tradeGroupId),
    onSuccess: () => {
      // Refetch the full record rather than trusting the inline body (wire-contracts.md).
      queryClient.invalidateQueries({ queryKey: ['active-trade-groups-details'] });
      queryClient.invalidateQueries({ queryKey: ['trade-group-details', tradeGroupId] });
      onSuccess();
      onClose();
    },
    onError: (error) => {
      console.error(`Error ${isPause ? 'pausing' : 'resuming'} trade group:`, error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    },
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cancel-trade-group-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isPause ? 'Pause Trade Group' : 'Resume Trade Group'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {isPause ? (
            <>
              <p className="modal-description">
                Paused groups open no new positions; an operator-paused group will NOT auto-resume
                and must be resumed manually. Any open in-flight position continues to be managed to
                completion.
              </p>
              <div className="form-group">
                <label>Reason (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g., halting ahead of high-impact news"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="form-input"
                />
                <small className="form-hint">Optional operator note recorded with the pause</small>
              </div>
            </>
          ) : (
            <p className="modal-description">
              Resume this operator-paused group, returning it to active monitoring (WATCHING). It
              will once again be eligible to open new positions.
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="modal-pill-button modal-pill-cancel"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            className="modal-pill-button modal-pill-primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? isPause
                ? 'Pausing...'
                : 'Resuming...'
              : isPause
              ? 'Pause'
              : 'Resume'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperatorPauseResumeModal;
